import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { PriorityPill } from "@/components/app/ScoreBadge";
import { Button } from "@/components/ui/button";
import { Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = ["Open", "In Progress", "Completed", "Rejected", "On Hold"];
const COL_TONE = {
  "Open": "border-slate-200",
  "In Progress": "border-blue-200",
  "Completed": "border-emerald-200",
  "Rejected": "border-rose-200",
  "On Hold": "border-amber-200",
};

export default function Execution() {
  const { selectedBrandId } = useBrands();
  const [items, setItems] = useState([]);

  const load = () => {
    if (!selectedBrandId) return;
    api.get(`/tasks?brand_id=${selectedBrandId}`).then((r) => setItems(r.data.items || []));
  };
  useEffect(load, [selectedBrandId]);

  const move = async (id, status) => {
    try {
      await api.patch(`/tasks/${id}`, { status });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (e) { toast.error(extractError(e)); }
  };

  const grouped = useMemo(() => {
    const o = Object.fromEntries(COLUMNS.map((c) => [c, []]));
    for (const t of items) (o[t.status] || o.Open).push(t);
    return o;
  }, [items]);

  const regenerate = async () => {
    try {
      await api.post(`/recommendations/regenerate?brand_id=${selectedBrandId}`);
      toast.success("Tasks regenerated"); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Execution</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Weekly Execution Plan</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} tasks · drag-to-status with the dropdown · ownership by role.</p>
        </div>
        <Button data-testid="exec-regenerate" variant="outline" onClick={regenerate} className="gap-2">
          <RefreshCw className="h-3.5 w-3.5" /> Regenerate
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="execution-kanban">
        {COLUMNS.map((col) => (
          <div key={col} className={`bg-slate-50/40 border-2 ${COL_TONE[col]} rounded-xl p-3 min-h-[400px]`} data-testid={`exec-col-${col}`}>
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-700">{col}</span>
              <span className="text-[11px] font-semibold text-slate-500">{grouped[col].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[col].map((t) => (
                <div key={t.id} className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center gap-2 mb-1.5">
                    <PriorityPill priority={t.priority} />
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">{t.task_type}</span>
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 leading-snug">{t.task_title}</h4>
                  <p className="mt-1 text-xs text-slate-500 line-clamp-2">{t.task_description}</p>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                    <span className="capitalize">{(t.assigned_role || "").replace("_", " ")}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {t.due_date || "—"}</span>
                  </div>
                  <select
                    value={t.status}
                    onChange={(e) => move(t.id, e.target.value)}
                    data-testid={`exec-status-${t.id}`}
                    className="mt-3 w-full text-xs rounded-md border border-slate-200 bg-white px-2 py-1"
                  >
                    {COLUMNS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              {!grouped[col].length && <div className="text-[11px] text-slate-400 italic px-1">No tasks</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
